import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, ShieldCheck, ShieldOff, MessageCircle, Mail, Phone, Lock } from "lucide-react";
import { CATEGORIES, INTERESTS, STATUSES, CONSENT_SOURCES, catOf, consentInfo, fmtDate, labelOf } from "@/lib/crm";

function Chip({ active, locked, onClick, children, testid, cls }) {
  return (
    <button type="button" onClick={locked ? undefined : onClick} data-testid={testid} aria-pressed={active}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${active ? `${cls || "bg-brand/15 text-brand"} border-transparent` : "border-border bg-transparent text-muted-foreground hover:bg-brema-hover"} ${locked ? "cursor-default" : ""}`}>
      {locked && <Lock className="h-3 w-3" />} {children}
    </button>
  );
}

/** Blocco consenso commerciale: stato + fonte + data; registrazione/revoca manuali, revoca automatica su opt-out. */
function ConsentBlock({ card, onPatch, busy }) {
  const [mode, setMode] = useState(null); // "grant" | "revoke"
  const [source, setSource] = useState("whatsapp");
  const [note, setNote] = useState("");
  const info = consentInfo(card.consent);
  const confirm = async () => {
    await onPatch({ consent_status: mode === "grant" ? "granted" : "revoked", consent_source: source, consent_note: note });
    setMode(null); setNote("");
  };
  return (
    <div className="rounded-lg border border-border bg-brema-bg/40 p-3" data-testid="contact-consent-block">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Consenso comunicazioni commerciali</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${info.cls}`} data-testid="contact-consent-badge">
              {card.consent?.status === "granted" ? <ShieldCheck className="h-3 w-3" /> : <ShieldOff className="h-3 w-3" />} {info.label}
            </span>
            <span className="text-xs text-muted-foreground" data-testid="contact-consent-detail">{info.detail}</span>
          </div>
          {card.consent?.note && <p className="mt-1 text-xs text-muted-foreground">Nota: {card.consent.note}</p>}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {card.consent?.status !== "granted" && (
            <Button type="button" size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={() => { setMode("grant"); setSource("whatsapp"); }} data-testid="contact-consent-grant-btn" disabled={busy}>
              <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Registra consenso
            </Button>
          )}
          {card.consent?.status === "granted" && (
            <Button type="button" size="sm" variant="outline" className="h-8 rounded-full text-xs text-red-600 dark:text-red-300" onClick={() => { setMode("revoke"); setSource("verbale"); }} data-testid="contact-consent-revoke-btn" disabled={busy}>
              <ShieldOff className="mr-1 h-3.5 w-3.5" /> Revoca
            </Button>
          )}
        </div>
      </div>
      {mode && (
        <div className="mt-3 grid gap-2 rounded-md border border-brand/30 bg-brand/5 p-3 sm:grid-cols-[180px_1fr_auto]" data-testid="contact-consent-form">
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="h-9 text-xs" data-testid="contact-consent-source"><SelectValue /></SelectTrigger>
            <SelectContent>{CONSENT_SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={mode === "grant" ? "Es. ha chiesto di ricevere novità via WhatsApp" : "Motivo della revoca (facoltativo)"} className="h-9 text-xs" data-testid="contact-consent-note" />
          <div className="flex gap-1.5">
            <Button type="button" size="sm" className="h-9 rounded-full bg-primary text-xs text-primary-foreground" onClick={confirm} disabled={busy} data-testid="contact-consent-confirm">
              {mode === "grant" ? "Conferma consenso" : "Conferma revoca"}
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-9 rounded-full text-xs" onClick={() => setMode(null)} data-testid="contact-consent-cancel">Annulla</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Scheda contatto unificata: legge/scrive sul record esistente (lead, ambassador, testata, rivenditore B2B) o sulla scheda base WhatsApp. */
export function ContactCard({ ctype, cid, onSaved, compact = false }) {
  const [card, setCard] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/contacts/${ctype}/${cid}`).then(({ data }) => {
      setCard(data.contact);
      const c = data.contact;
      setForm({ name: c.name, email: c.email, whatsapp: c.whatsapp, categories: c.categories, interests: c.interests, status: c.status, notes: c.notes });
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Scheda non disponibile")).finally(() => setLoading(false));
  };
  useEffect(load, [ctype, cid]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = async (body) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/contacts/${ctype}/${cid}`, body);
      setCard(data.contact);
      const c = data.contact;
      setForm({ name: c.name, email: c.email, whatsapp: c.whatsapp, categories: c.categories, interests: c.interests, status: c.status, notes: c.notes });
      toast.success("Scheda contatto aggiornata");
      onSaved?.(c);
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio fallito");
      return false;
    } finally { setSaving(false); }
  };

  if (loading || !card || !form) return <div className="py-6 text-center text-muted-foreground" data-testid="contact-card-loading"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;

  const dirty = JSON.stringify(form) !== JSON.stringify({ name: card.name, email: card.email, whatsapp: card.whatsapp, categories: card.categories, interests: card.interests, status: card.status, notes: card.notes });
  const toggle = (key, v) => setForm((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] }));
  const save = () => patch({ name: form.name, email: form.email, whatsapp: form.whatsapp, categories: form.categories, interests: form.interests, status: form.status, notes: form.notes });

  return (
    <div className="space-y-3" data-testid="contact-card">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="rounded-full bg-brema-bg px-2 py-0.5 font-semibold text-foreground" data-testid="contact-card-type">{card.type_label}</span>
        {card.module_status && <span>Stato nel modulo: <b className="text-foreground">{card.module_status}</b></span>}
        <span className="inline-flex items-center gap-1" data-testid="contact-card-activity"><MessageCircle className="h-3 w-3" /> {card.wa_count || 0} msg WhatsApp · {card.messages_count || 0} totali{card.last_message_at ? ` · ultimo ${fmtDate(card.last_message_at)}` : ""}</span>
        {card.opted_out && <span className="rounded-full bg-red-500/15 px-2 py-0.5 font-semibold text-red-700 dark:text-red-300">Non contattare</span>}
      </div>

      <div className={`grid gap-3 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-3"}`}>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Nome</Label>
          <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} disabled={!card.name_editable} className="h-9 text-sm" data-testid="contact-name" />
        </div>
        <div className="space-y-1">
          <Label className="flex items-center gap-1 text-xs text-muted-foreground"><Phone className="h-3 w-3" /> Numero WhatsApp</Label>
          <Input value={form.whatsapp} onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))} placeholder="+39 348 1234567" className="h-9 text-sm" data-testid="contact-whatsapp" />
        </div>
        <div className="space-y-1">
          <Label className="flex items-center gap-1 text-xs text-muted-foreground"><Mail className="h-3 w-3" /> E-mail</Label>
          <Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="nome@esempio.com" className="h-9 text-sm" data-testid="contact-email" />
        </div>
      </div>
      {card.alt_phones?.length > 0 && <p className="text-[11px] text-muted-foreground">Altri numeri collegati: {card.alt_phones.join(", ")}</p>}

      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">Categorie (anche più di una)</Label>
        <div className="flex flex-wrap gap-1.5" data-testid="contact-categories">
          {CATEGORIES.map((c) => {
            const locked = card.type !== "wa_contact" && c.value === card.base_category;
            return <Chip key={c.value} active={form.categories.includes(c.value)} locked={locked} cls={c.cls} onClick={() => toggle("categories", c.value)} testid={`contact-cat-${c.value}`}>{c.label}</Chip>;
          })}
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">Interessi</Label>
        <div className="flex flex-wrap gap-1.5" data-testid="contact-interests">
          {INTERESTS.map((i) => <Chip key={i.value} active={form.interests.includes(i.value)} onClick={() => toggle("interests", i.value)} testid={`contact-int-${i.value}`}>{i.label}</Chip>)}
        </div>
      </div>
      <div className={`grid gap-3 ${compact ? "" : "sm:grid-cols-[200px_1fr]"}`}>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Stato contatto</Label>
          <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
            <SelectTrigger className="h-9 text-sm" data-testid="contact-status"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Note</Label>
          <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="min-h-[70px] text-sm" placeholder="Note operative, accordi, preferenze…" data-testid="contact-notes" />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground">
          {card.type === "wa_contact" ? "Scheda base creata dal numero WhatsApp." : `Le modifiche aggiornano direttamente l'anagrafica ${labelOf(CATEGORIES, card.base_category)} (nessun duplicato).`}
        </p>
        <Button type="button" size="sm" onClick={save} disabled={saving || !dirty} className="shrink-0 rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40" data-testid="contact-save-btn">
          {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />} Salva scheda
        </Button>
      </div>

      <ConsentBlock card={card} onPatch={patch} busy={saving} />
      <p className="text-[10px] text-muted-foreground">Categoria principale: <span className={`rounded-full px-1.5 py-0.5 ${catOf(card.base_category).cls}`}>{catOf(card.base_category).label}</span>{card.created_at ? ` · creato ${fmtDate(card.created_at)}` : ""}</p>
    </div>
  );
}
